import { useEffect, useState } from "react";
import type { FxKind } from "../lib/creative-fx.js";

/**
 * Live animated FX overlay for the editor preview, driven by the resolved `look`
 * selection (rain / lightning / radial focus blur). Rendered inside `.reel-shell`
 * ABOVE the hybrid `<video>` and creative overlay; clipped to the card's rounded
 * corners via `border-radius: inherit` + `overflow: hidden` on each layer so the
 * backdrop-filter blur cannot bleed into square corners.
 *
 * GPU-cheap (transform / opacity / backdrop-filter only). Respects
 * `prefers-reduced-motion`: animations fall back to a representative static frame
 * and the lightning strobe timer is disabled.
 */
export function CreativeFxLayer({
  fx,
  reducedMotion,
  playing = false,
}: {
  /** One effect, a stack of effects, or null. Effects render layered together. */
  fx: FxKind | FxKind[] | null;
  reducedMotion: boolean;
  /**
   * Whether the reel is playing. Effects are part of the reel's MOTION: they
   * animate while it plays and FREEZE when it pauses or ends (rain stops
   * mid-fall, no new lightning strikes), so a paused/ended frame is a still
   * frame. Also lets lightning fire its first strike the moment playback starts.
   */
  playing?: boolean;
}) {
  const list = fx == null ? [] : Array.isArray(fx) ? fx : [fx];
  if (list.length === 0) return null;
  // Focus blur renders in its OWN root, SEPARATE from rain/lightning. Its
  // backdrop-filter must sample the video cleanly; the rain haze / lightning
  // brighten use mix-blend-mode, which isolates their group and would otherwise
  // cancel the blur when stacked. Keeping blur in a sibling root (painted first,
  // underneath) fixes the "blur disappears when lightning is on" bug.
  const order: FxKind[] = ["focus-blur", "rain", "lightning"];
  const active = order.filter((k) => list.includes(k));
  const overlays = active.filter((k) => k !== "focus-blur");
  return (
    <>
      {active.includes("focus-blur") ? (
        <div
          className="creative-fx-blur-root"
          data-testid="creative-fx-blur"
          data-playing={playing ? "1" : "0"}
          aria-hidden
        >
          <FocusBlurFx reducedMotion={reducedMotion} />
        </div>
      ) : null}
      <div
        className="creative-fx-root"
        data-testid="creative-fx"
        data-fx={active.join(" ")}
        data-fx-count={active.length}
        data-reduced={reducedMotion ? "1" : "0"}
        data-playing={playing ? "1" : "0"}
        aria-hidden
      >
        {overlays.includes("rain") ? <RainFx reducedMotion={reducedMotion} /> : null}
        {overlays.includes("lightning") ? (
          <LightningFx reducedMotion={reducedMotion} playing={playing} />
        ) : null}
      </div>
    </>
  );
}

/**
 * Radial focus blur — a full-bleed layer whose backdrop-filter blurs the video
 * behind it, with a radial mask that CLEARS the center so the subject (cat, ~50%
 * x / 52% y) stays sharp while the edges/background soften. Optional gentle pulse.
 */
function FocusBlurFx({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <div
      className={`fx-focus-blur${reducedMotion ? " reduced" : ""}`}
      data-testid="fx-focus-blur"
    />
  );
}

/**
 * Animated rain — dense, near-vertical layered streaks (far/mid/near) falling at
 * different speeds + opacities, over a soft storm haze, with a faint splash band
 * near the bottom. Reads clearly as rain rather than a few diagonal lines.
 *
 * Each layer gets deterministic pseudo-random offsets (per-seed) so streaks
 * don't all START aligned — they begin at scattered heights with varied
 * animation-delays, creating an organic scattered look.
 */
function RainFx({ reducedMotion }: { reducedMotion: boolean }) {
  // Deterministic seeded pseudo-random for consistent offsets across re-renders
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  // Far layer offsets and delay
  const farR1 = seededRandom(11);
  const farR2 = seededRandom(12);
  const farR3 = seededRandom(13);
  const farDelay = ((farR1 - 0.5) * 0.4).toFixed(3);
  const farOffsetY = Math.round(farR2 * 50 - 25); // -25 to 25px vertical scatter
  const farOffsetY2 = Math.round(farR3 * 40 - 20);

  // Mid layer offsets and delay
  const midR1 = seededRandom(21);
  const midR2 = seededRandom(22);
  const midR3 = seededRandom(23);
  const midDelay = ((midR1 - 0.5) * 0.4).toFixed(3);
  const midOffsetY = Math.round(midR2 * 50 - 25);
  const midOffsetY2 = Math.round(midR3 * 40 - 20);

  // Near layer offsets and delay
  const nearR1 = seededRandom(31);
  const nearR2 = seededRandom(32);
  const nearR3 = seededRandom(33);
  const nearDelay = ((nearR1 - 0.5) * 0.4).toFixed(3);
  const nearOffsetY = Math.round(nearR2 * 50 - 25);
  const nearOffsetY2 = Math.round(nearR3 * 40 - 20);

  return (
    <div className={`fx-rain${reducedMotion ? " reduced" : ""}`} data-testid="fx-rain">
      <div className="fx-rain-haze" />
      <div
        className="fx-rain-layer fx-rain-far"
        style={
          {
            "--rain-far-offset-x": "0",
            "--rain-far-offset-y": `${farOffsetY}px`,
            "--rain-far-offset-x2": "9px",
            "--rain-far-offset-y2": `${farOffsetY2}px`,
            "--rain-far-delay": `${farDelay}s`,
          } as React.CSSProperties
        }
      />
      <div
        className="fx-rain-layer fx-rain-mid"
        style={
          {
            "--rain-mid-offset-x": "0",
            "--rain-mid-offset-y": `${midOffsetY}px`,
            "--rain-mid-offset-x2": "13px",
            "--rain-mid-offset-y2": `${midOffsetY2}px`,
            "--rain-mid-delay": `${midDelay}s`,
          } as React.CSSProperties
        }
      />
      <div
        className="fx-rain-layer fx-rain-near"
        style={
          {
            "--rain-near-offset-x": "0",
            "--rain-near-offset-x2": "19px",
            "--rain-near-offset-y": `${nearOffsetY}px`,
            "--rain-near-offset-y2": `${nearOffsetY2}px`,
            "--rain-near-delay": `${nearDelay}s`,
          } as React.CSSProperties
        }
      />
      <div className="fx-rain-splash" />
    </div>
  );
}

/**
 * Lightning — occasional, well-timed strikes on a randomized interval. Each
 * strike plays a quick DOUBLE-flash + a short screen-brighten and reveals a clean
 * SVG bolt (all via a CSS keyframe on the `.strike` class, so it's GPU-cheap and
 * self-timed). Under reduced motion, a single static dim bolt/flash is shown with
 * no strobing.
 */
function LightningFx({
  reducedMotion,
  playing,
}: {
  reducedMotion: boolean;
  playing: boolean;
}) {
  const [strike, setStrike] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      setStrike(true);
      return;
    }
    // Lightning only strikes while the reel is PLAYING; a paused/ended reel holds
    // a calm frame (no strike) rather than strobing over a still image.
    if (!playing) {
      setStrike(false);
      return;
    }
    let flashTimer: ReturnType<typeof setTimeout> | undefined;
    let offTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const fire = () => {
      if (cancelled) return;
      setStrike(true);
      // Hold long enough for the double-flash keyframe (~0.7s) to complete, then
      // wait a randomized gap (2.6–5.2s) before the next strike.
      offTimer = setTimeout(() => {
        if (cancelled) return;
        setStrike(false);
        const delay = 2600 + Math.random() * 2600;
        flashTimer = setTimeout(fire, delay);
      }, 760);
    };
    // Strike IMMEDIATELY when lightning starts (or playback resumes) — no dead
    // gap before the first bolt.
    fire();
    return () => {
      cancelled = true;
      if (flashTimer) clearTimeout(flashTimer);
      if (offTimer) clearTimeout(offTimer);
    };
  }, [reducedMotion, playing]);

  return (
    <div
      className={`fx-lightning${reducedMotion ? " reduced" : ""}${strike ? " strike" : ""}`}
      data-testid="fx-lightning"
      data-strike={strike ? "1" : "0"}
    >
      <div className="fx-lightning-brighten" />
      <div className="fx-lightning-flash" />
      <svg
        className="fx-lightning-bolt"
        viewBox="0 0 100 200"
        preserveAspectRatio="xMidYMin meet"
        aria-hidden
      >
        {/* Cleaner forked bolt — tapering zig-zag with a short branch. */}
        <path
          d="M56 0 L40 78 L54 78 L44 132 L60 128 L34 200 L46 118 L32 118 L48 66 L36 66 Z"
          fill="#fdfcff"
          stroke="#cfe0ff"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        <path
          d="M50 96 L64 108 L55 112"
          fill="none"
          stroke="#eaf1ff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
